import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ClosingDocumentViewer from "./ClosingDocumentViewer";
import {
    BrokerTypes,
    MockOfferStruct,
} from "graphql/__generated__/globalTypes";
import Modal from "components/layout/modal";
import BrokerPermissionModal from "components/layout/broker-permission-modal";
import OfferClaims from "./OfferClaims";
import OfferPayments from "./OfferPayments";
import ThreadContainer from "./ThreadContainer";

type Props = {
    offer: MockOfferStruct | null;
    refresh?: () => void;
    brokerName: BrokerTypes;
};

const OffersActionMenu = ({ offer, refresh, brokerName }: Props) => {
    const { t } = useTranslation();
    const [openPayments, setOpenPayments] = useState(false);
    const [openClaims, setOpenClaims] = useState(false);
    const [openDocViewer, setOpenDocViewer] = useState(false);
    const [showThreadContainer, setShowThreadContainer] = useState(false);
    const [showBrokerPermission, setShowBrokerPermission] = useState(false);

    const isRestrictedBroker =
        brokerName === BrokerTypes.Kek || brokerName === BrokerTypes.Irisk;

    const handleViewDocs = () => {
        if (isRestrictedBroker) {
            setShowBrokerPermission(true);
        } else {
            setOpenDocViewer(true);
        }
    };

    const unreadMessageCount = useMemo(
        () =>
            offer?.offer?.unread_messages?.filter((msg) => msg !== null)
                .length || 0,
        [offer?.offer?.unread_messages],
    );

    const unreadMessageIds = useMemo(
        () =>
            (offer?.offer?.unread_messages
                ?.filter((msg) => msg !== null)
                .map((msg) => msg?.id)
                .filter(Boolean) as string[]) || [],
        [offer?.offer?.unread_messages],
    );

    const openThreadContainer = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowThreadContainer(true);
    };

    return (
        <Fragment>
            <div className="flex space-x-3">
                <button
                    type="button"
                    onClick={handleViewDocs}
                    className="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100 hover:border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
                    <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                    {t("menu_items.view_docs")}
                </button>

                <button
                    type="button"
                    onClick={() => setOpenClaims(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-orange-100 bg-[#fff3cd]  px-3 py-2 text-sm font-medium text-[#856404] transition-colors hover:bg-[#FFE08A]  focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2">
                    <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                    </svg>
                    {t("menu_items.view_claims")}
                </button>
                {offer?.offer?.offer_published && (
                    <button
                        type="button"
                        onClick={openThreadContainer}
                        className="inline-flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-200 px-3 py-2 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 relative">
                        <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                        </svg>
                        {t("common.buttons.thread")}
                        {unreadMessageCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-orange-500 rounded-full">
                                {unreadMessageCount}
                            </span>
                        )}
                    </button>
                )}

                {offer?.offer?.payment_status !== "UNPAID" && (
                    <button
                        type="button"
                        onClick={() => setOpenPayments(true)}
                        className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                        <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                            />
                        </svg>
                        {t("common.buttons.view_payments")}
                    </button>
                )}
            </div>

            <BrokerPermissionModal
                open={showBrokerPermission}
                setOpen={setShowBrokerPermission}
                brokerName={brokerName}
            />

            <ClosingDocumentViewer
                open={openDocViewer}
                setOpen={setOpenDocViewer}
                offerId={offer?.offersoffer_id || ""}
                offer={offer}
                reinsurerId={offer?.reinsurer?.reinsurer_id}
            />

            <Modal
                open={openClaims}
                setOpen={() => setOpenClaims(false)}
                title={t("modal_titles.claims")}
                description={t("modal_descriptions.claims")}
                size="6xl"
                onClose={refresh}>
                <OfferClaims offer={offer} claims={offer?.offer?.offer_claim} />
            </Modal>

            <Modal
                open={openPayments}
                setOpen={() => setOpenPayments(false)}
                title={t("modal_titles.payments")}
                description={t("modal_descriptions.payments")}
                size="6xl"
                onClose={refresh}>
                <OfferPayments payments={offer?.offer_participant_payment} />
            </Modal>
            <ThreadContainer
                open={showThreadContainer}
                setOpen={setShowThreadContainer}
                offer={offer?.offer || undefined}
                offerDetail={offer?.offer_detail || undefined}
                offerPublished={offer?.offer?.offer_published || undefined}
                participationId={
                    offer?.offer?.offer_published?.offer_published_participant
                        ?.offer_published_participant_id!
                }
                unreadMessageIds={unreadMessageIds}
                refetch={refresh}
                brokerName={brokerName}
            />
        </Fragment>
    );
};

export default OffersActionMenu;
